const DATA_MANAGER_SORT_COLLATOR = new Intl.Collator("fr-CA", {
  numeric: true,
  sensitivity: "base",
});

function normalizeDataManagerSortDirection(direction, fallbackDirection = "asc") {
  if (direction === "asc" || direction === "desc") {
    return direction;
  }

  return fallbackDirection === "desc" ? "desc" : "asc";
}

function resolveComparableSortValue(value) {
  if (value === null || value === undefined) {
    return { kind: "string", missing: true, value: "" };
  }

  if (value instanceof Date) {
    const timestamp = value.valueOf();
    return Number.isFinite(timestamp)
      ? { kind: "number", missing: false, value: timestamp }
      : { kind: "string", missing: true, value: "" };
  }

  if (typeof value === "number") {
    return Number.isFinite(value)
      ? { kind: "number", missing: false, value }
      : { kind: "string", missing: true, value: "" };
  }

  if (typeof value === "boolean") {
    return { kind: "number", missing: false, value: value ? 1 : 0 };
  }

  const resolvedValue = String(value).trim();
  return resolvedValue
    ? { kind: "string", missing: false, value: resolvedValue }
    : { kind: "string", missing: true, value: "" };
}

function compareDataManagerSortValues(leftValue, rightValue, directionMultiplier) {
  const normalizedLeftValue = resolveComparableSortValue(leftValue);
  const normalizedRightValue = resolveComparableSortValue(rightValue);

  if (normalizedLeftValue.missing && normalizedRightValue.missing) {
    return 0;
  }

  if (normalizedLeftValue.missing) {
    return 1;
  }

  if (normalizedRightValue.missing) {
    return -1;
  }

  let comparison = 0;
  if (normalizedLeftValue.kind === "number" && normalizedRightValue.kind === "number") {
    comparison = normalizedLeftValue.value - normalizedRightValue.value;
  } else {
    comparison = DATA_MANAGER_SORT_COLLATOR.compare(
      String(normalizedLeftValue.value),
      String(normalizedRightValue.value),
    );
  }

  return comparison === 0 ? 0 : comparison * directionMultiplier;
}

export function resolveInitialOrdersSortState(dispatchPeriod = "day") {
  const normalizedDispatchPeriod = typeof dispatchPeriod === "string"
    ? dispatchPeriod.trim().toLowerCase()
    : "";

  if (normalizedDispatchPeriod === "evening" || normalizedDispatchPeriod === "soir") {
    return { direction: "desc", key: "delivery_period" };
  }

  return { direction: "asc", key: null };
}

export function resolveNextDataManagerSortState(currentSortState, nextSortKey, defaultDirection = "asc") {
  const normalizedDefaultDirection = normalizeDataManagerSortDirection(defaultDirection);
  const currentSortKey = typeof currentSortState?.key === "string" ? currentSortState.key : "";
  const currentSortDirection = normalizeDataManagerSortDirection(currentSortState?.direction, normalizedDefaultDirection);

  if (!nextSortKey) {
    return { direction: normalizedDefaultDirection, key: null };
  }

  if (currentSortKey !== nextSortKey) {
    return { direction: normalizedDefaultDirection, key: nextSortKey };
  }

  if (currentSortDirection === "asc") {
    return { direction: "desc", key: nextSortKey };
  }

  return { direction: normalizedDefaultDirection, key: null };
}

export function resolveDataManagerHeaderAriaSort(sortState, columnKey) {
  if (sortState?.key !== columnKey) {
    return "none";
  }

  return normalizeDataManagerSortDirection(sortState?.direction) === "desc"
    ? "descending"
    : "ascending";
}

export function resolveDataManagerActiveSortState(sortState, columnKey) {
  const isActiveSort = sortState?.key === columnKey;

  return {
    direction: isActiveSort
      ? normalizeDataManagerSortDirection(sortState?.direction)
      : null,
    isActiveSort,
  };
}

export const DATA_MANAGER_ACTIVE_SORT_HEADER_CLASS_NAME = "relative z-[11] !bg-accent text-white ring-1 ring-inset ring-accent shadow-[inset_0_-3px_0_0_var(--color-accent-strong)]";
export const DATA_MANAGER_ACTIVE_SORT_LABEL_CLASS_NAME = "font-bold text-white";
export const DATA_MANAGER_ACTIVE_SORT_ICON_WRAP_CLASS_NAME = "inline-flex shrink-0 items-center justify-center rounded-full bg-white/15 p-0.5 text-white";

export function sortDataManagerRows(rows, sortState, getSortValue) {
  const sortKey = typeof sortState?.key === "string" ? sortState.key : "";
  if (!sortKey) {
    return Array.isArray(rows) ? rows : [];
  }

  const directionMultiplier = normalizeDataManagerSortDirection(sortState?.direction) === "desc" ? -1 : 1;
  return [...(Array.isArray(rows) ? rows : [])].sort((leftRow, rightRow) => (
    compareDataManagerSortValues(
      getSortValue(leftRow, sortKey),
      getSortValue(rightRow, sortKey),
      directionMultiplier,
    )
  ));
}
